(() => {
  const emit = (name, detail) => {
    window.dispatchEvent(new CustomEvent(name, { detail }));
    try {
      window.Shopify?.analytics?.publishCustomEvent?.(
        `field_signal:${name.replace('fieldsignal:', '')}`,
        detail
      );
    } catch (_) {
      // Analytics is optional and must not affect purchase behavior.
    }
  };

  function selectedTab(landing) {
    return landing.querySelector('[role="tab"][aria-selected="true"]');
  }

  function formatMoney(landing, cents) {
    const currency = landing.dataset.fsCurrency || window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
      }).format(Math.max(0, Number(cents) || 0) / 100);
    } catch (_) {
      return `$${(Math.max(0, Number(cents) || 0) / 100).toFixed(2)}`;
    }
  }

  function setStickyFromPanel(landing, version) {
    const sticky = landing.querySelector('[data-fs-pdp-sticky]');
    const panel = landing.querySelector(
      `.fs-pdp-purchase-panel[data-fs-ad-version="${version}"]`
    );
    const tab = landing.querySelector(
      `[role="tab"][data-fs-ad-version="${version}"]`
    );
    if (!sticky || !panel) return;

    const selectedOffer =
      panel.querySelector('[data-fs-ad-offer-selected]') ||
      panel.querySelector('[data-fs-ad-offer]');
    const productForm = panel.querySelector('[data-fs-ad-product-form]');
    const planChoice = panel.querySelector('[data-fs-ad-protection-choice="plan"]');
    const hardwareCents = Number(selectedOffer?.dataset.fsAdPriceCents || 0);
    const protectionCents = planChoice?.checked && !planChoice.disabled
      ? Number(planChoice.dataset.fsAdProtectionPriceCents || 0)
      : 0;
    const synchronizedTotal = Number(productForm?.dataset.fsOrderTotalCents);
    const orderTotalCents = Number.isFinite(synchronizedTotal)
      ? synchronizedTotal
      : hardwareCents + protectionCents;
    const units = Number(selectedOffer?.dataset.fsAdUnits || 1);
    const versionNode = sticky.querySelector('[data-fs-pdp-sticky-version]');
    const titleNode = sticky.querySelector('[data-fs-pdp-sticky-title]');
    const imageNode = sticky.querySelector('[data-fs-pdp-sticky-image]');
    const priceNode = sticky.querySelector('[data-fs-pdp-sticky-price]');
    const savingsNode = sticky.querySelector('[data-fs-pdp-sticky-savings]');
    const planNode = sticky.querySelector('[data-fs-pdp-sticky-plan]');

    const configuration = version === 'pro' ? 'Pro' : 'Standard';
    if (versionNode) versionNode.textContent = `${configuration} · ${units} ${units === 1 ? 'pair' : 'pairs'}`;
    if (titleNode && tab?.dataset.fsPdpTitle) titleNode.textContent = tab.dataset.fsPdpTitle;
    if (imageNode && tab?.dataset.fsPdpImage) {
      imageNode.src = tab.dataset.fsPdpImage;
      imageNode.alt = tab.dataset.fsPdpAlt || `Field Signal ${configuration}`;
    }
    if (priceNode) priceNode.textContent = formatMoney(landing, orderTotalCents);
    if (savingsNode) {
      savingsNode.textContent = 'Order total';
      savingsNode.hidden = false;
    }
    if (planNode) {
      const planPrice = panel.querySelector('[data-fs-ad-protection-price]')?.textContent?.trim() || '';
      planNode.textContent = productForm?.dataset.fsFreeProtection === 'true' ? 'Lifetime protection included FREE' : planChoice?.checked
        ? `Lifetime protection selected${planPrice ? ` · +${planPrice}` : ''}`
        : 'Lifetime protection removed';
    }
  }

  function syncSticky(landing) {
    const version = selectedTab(landing)?.dataset.fsAdVersion || 'pro';
    setStickyFromPanel(landing, version);
  }

  function setupGallery(landing) {
    landing.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-fs-pdp-gallery-thumb]');
      if (!thumb || !landing.contains(thumb)) return;

      const gallery = thumb.closest('.fs-pdp-gallery');
      const lead = gallery?.querySelector('[data-fs-pdp-gallery-lead]');
      const source = thumb.dataset.fsPdpGallerySrc;
      if (!gallery || !lead || !source) return;

      lead.src = source;
      lead.alt = thumb.dataset.fsPdpGalleryAlt || lead.alt;
      gallery.querySelectorAll('[data-fs-pdp-gallery-thumb]').forEach((node) => {
        const selected = node === thumb;
        node.classList.toggle('is-selected', selected);
        node.setAttribute('aria-pressed', String(selected));
      });
      emit('fieldsignal:gallery_interaction', {
        image: source,
        product: gallery.dataset.fsAdVersion || '',
      });
    });
  }

  function setupSticky(landing) {
    const sticky = landing.querySelector('[data-fs-pdp-sticky]');
    const buy = landing.querySelector('.fs-pdp-stage__purchase');
    if (!sticky || !buy) return;
    const pageWrapper = document.querySelector('.page-wrapper');

    const updateVisibility = () => {
      const desktopScroller =
        window.matchMedia('(min-width: 990px)').matches && pageWrapper;
      const scrollTop = desktopScroller ? pageWrapper.scrollTop : window.scrollY;
      const scrollerTop = desktopScroller ? pageWrapper.getBoundingClientRect().top : 0;
      const buyTop = buy.getBoundingClientRect().top - scrollerTop + scrollTop;
      const compactViewport = window.matchMedia('(max-width: 749px)').matches;
      const threshold = compactViewport
        ? Math.max(0, buyTop - window.innerHeight * 0.25)
        : buyTop + 260;
      sticky.classList.toggle('is-visible', scrollTop > threshold);
    };

    window.addEventListener('scroll', updateVisibility, { passive: true });
    pageWrapper?.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility, { passive: true });
    sticky.querySelector('[data-fs-pdp-sticky-submit]')?.addEventListener('click', () => {
      const version = selectedTab(landing)?.dataset.fsAdVersion || 'pro';
      const panel = landing.querySelector(
        `.fs-pdp-purchase-panel[data-fs-ad-version="${version}"]`
      );
      const form = panel?.querySelector('[data-fs-ad-product-form]');
      if (!form) return;
      if (form.getAttribute('aria-busy') === 'true') return;
      const submitter = form.querySelector('[type="submit"]');
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitter || undefined);
      } else {
        submitter?.click();
      }
    });
    updateVisibility();
  }

  function setupInteractionSync(landing) {
    document.addEventListener('fieldsignal:purchase_state_changed', (event) => {
      const productForm = event.detail?.productForm;
      if (productForm && !landing.contains(productForm)) return;
      syncSticky(landing);
    });

    landing.querySelectorAll('details').forEach((detail) => {
      detail.addEventListener('toggle', () => {
        if (!detail.open) return;
        emit('fieldsignal:faq_toggle', {
          question: detail.querySelector('summary')?.textContent?.trim() || '',
        });
      });
    });

  }

  function initializeLanding(landing) {
    if (landing.dataset.fsPaidPdpInitialized === 'true') return;
    landing.dataset.fsPaidPdpInitialized = 'true';
    document.body.classList.add('fs-paid-pdp-active');
    setupGallery(landing);
    setupSticky(landing);
    setupInteractionSync(landing);
    syncSticky(landing);
  }

  function initialize() {
    document.querySelectorAll('[data-fs-paid-pdp]').forEach(initializeLanding);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
